import type { Social } from '.';

import { action } from '@play-co/replicant';
import { defineActions } from '../utils';
import { SocialSubFeature } from './social.ruleset';

export const socialActions = (
  Social: Social,
  { Inventory }: Social['$DEPENDENCIES'],
) =>
  defineActions<Social>()({
    /**
     * Update state.friends for cluster storage
     */
    updatePlatformFriends: action(
      (
        state,
        args: {
          friendIds: readonly string[];
        },
      ) => {
        const friends = args.friendIds.reduce((map, id) => {
          map[id] = {};
          return map;
        }, {});

        state.friends = friends;
      },
    ),

    recordInvite: action((state, friendIds: string[], api) => {
      const now = api.date.now();
      const { getExcludedFriendsForInvite } = Social.getters(state);
      const excludedFriends = getExcludedFriendsForInvite(friendIds, now);
      if (excludedFriends.length > 0) {
        api.reportError(
          new Error(`Attempting to record invite for excluded friends`),
          { excludedFriends },
        );

        // Remove any already invited friends to prevent exploit
        friendIds = friendIds.filter((id) => !excludedFriends.includes(id));
      }

      Social.modifiers(state).recordInvites(friendIds, now);
    }),

    claimInviteRewards: action((state, subFeature: SocialSubFeature, api) => {
      const claimedRewardCount = Social.modifiers(
        state,
      ).setAllReferralRewardsClaimed();

      const { getCurrencies } = Inventory.getters(state);
      const now = api.date.now();
      const oldCurrencies = getCurrencies(now);
      const inviteReward = Social.getters(state).getInviteReward(subFeature);
      const amount = claimedRewardCount * inviteReward.value;

      // Give energy or gems depends on feature type
      switch (inviteReward.type) {
        case 'energy':
          Inventory.modifiers(state).addEnergy(amount, api.date.now());
          break;
        case 'gems':
          Inventory.modifiers(state).addFreeGems(amount);
          break;
      }

      // Send CurrencyGrant event
      api.sendAnalyticsEvents([
        Inventory.ruleset.getCurrencyGrantEvent(
          {
            feature: 'invite_reward',
          },
          {
            old: oldCurrencies,
            new: getCurrencies(now),
          },
        ),
      ]);

      return { amount, ...inviteReward };
    }),

    claimShareRewards: action((state, subFeature: SocialSubFeature, api) => {
      const { getCurrencies } = Inventory.getters(state);
      const now = api.date.now();
      const oldCurrencies = getCurrencies(now);
      const inviteReward = Social.getters(state).getInviteReward(subFeature);
      const amount = inviteReward.value;

      // Give energy or gems depends on feature type
      switch (inviteReward.type) {
        case 'energy':
          Inventory.modifiers(state).addEnergy(amount, api.date.now());
          break;
        case 'gems':
          Inventory.modifiers(state).addFreeGems(amount);
          break;
      }

      // Send CurrencyGrant event
      api.sendAnalyticsEvents([
        Inventory.ruleset.getCurrencyGrantEvent(
          {
            feature: 'share_reward',
          },
          {
            old: oldCurrencies,
            new: getCurrencies(now),
          },
        ),
      ]);

      return { amount, ...inviteReward };
    }),

    /** Claim rewards for successful shares, that resulted in player conversion */
    claimShareSuccessRewards: action(
      (state, subFeature: SocialSubFeature, api) => {
        const claimedRewardCount = Social.modifiers(
          state,
        ).setAllReferralRewardsClaimed();
        const { getCurrencies } = Inventory.getters(state);
        const now = api.date.now();
        const oldCurrencies = getCurrencies(now);
        const inviteReward = Social.getters(state).getInviteReward(subFeature);
        const inviteRewardCap = Social.getters(state).getInviteRewardCap(
          subFeature,
        );

        let amount = inviteReward.value * claimedRewardCount;
        if (inviteRewardCap) amount = Math.min(amount, inviteRewardCap.value);

        // Give energy or gems depends on feature type
        switch (inviteReward.type) {
          case 'energy':
            Inventory.modifiers(state).addEnergy(amount, api.date.now());
            break;
          case 'gems':
            Inventory.modifiers(state).addFreeGems(amount);
            break;
        }

        // Send CurrencyGrant event
        api.sendAnalyticsEvents([
          Inventory.ruleset.getCurrencyGrantEvent(
            {
              feature: 'share_success_reward',
            },
            {
              old: oldCurrencies,
              new: getCurrencies(now),
            },
          ),
        ]);

        return { amount, ...inviteReward };
      },
    ),
  });
